import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type { ClientEvent, OptionKey, ParticipantState } from '@examatron/shared';
import { useSocket } from '../lib/socket';
import '../styles/session.css';

const tokenKey = (code: string) => `bns.participant.${code.toUpperCase()}`;

/**
 * Participant view for `/s/<CODE>`.
 *
 * A participant never chooses when to move — the instructor is the clock. This
 * component's whole job is to render whatever the current snapshot says and to
 * submit one answer per question.
 */
export function Play({ code }: { code: string }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(tokenKey(code)));
  const [form, setForm] = useState({ name: '', pin: '' });
  const [submitting, setSubmitting] = useState(false);

  /*
   * Replayed on every reconnect. A stored token resumes silently; otherwise the
   * socket stays open and unidentified until the join form is submitted, which
   * is what `null` means here.
   */
  const hello = useCallback((): ClientEvent | null => {
    if (token) return { type: 'resume', token };
    if (submitting && form.name && form.pin) {
      return { type: 'join', code, name: form.name, pin: form.pin };
    }
    return null;
  }, [token, submitting, form.name, form.pin, code]);

  const { state, connection, error, send } = useSocket<ParticipantState>(hello);

  // The server issues a resume token on join and on reconnect. Storing it is
  // what makes a refresh silent from then on.
  useEffect(() => {
    if (state?.resumeToken) {
      localStorage.setItem(tokenKey(code), state.resumeToken);
      setToken(state.resumeToken);
    }
  }, [state?.resumeToken, code]);

  // A rejected token is a dead end — drop it so the join form comes back rather
  // than retrying forever with something the server will not accept.
  useEffect(() => {
    if (error && /join again|expired/i.test(error)) {
      localStorage.removeItem(tokenKey(code));
      setToken(null);
      setSubmitting(false);
    }
  }, [error, code]);

  function join(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    send({ type: 'join', code, name: form.name, pin: form.pin });
  }

  if (!state) {
    return (
      <JoinForm
        code={code}
        form={form}
        setForm={setForm}
        onSubmit={join}
        error={error}
        connection={connection}
      />
    );
  }

  return <Live state={state} connection={connection} send={send} />;
}

function JoinForm({
  code,
  form,
  setForm,
  onSubmit,
  error,
  connection,
}: {
  code: string;
  form: { name: string; pin: string };
  setForm: (f: { name: string; pin: string }) => void;
  onSubmit: (e: FormEvent) => void;
  error: string | null;
  connection: string;
}) {
  const ready = form.name.trim().length > 0 && /^\d{4}$/.test(form.pin);

  return (
    <div className="signin">
      <form className="signin__card" onSubmit={onSubmit}>
        <div>
          <p className="eyebrow">Session {code.toUpperCase()}</p>
          <h1 style={{ fontSize: 34, marginTop: 'var(--s-2)' }}>Join</h1>
        </div>

        <div className="field">
          <label htmlFor="name">First name</label>
          <input
            id="name"
            autoComplete="given-name"
            autoFocus
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>

        <div className="field">
          <label htmlFor="pin">4-digit code</label>
          <input
            id="pin"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={form.pin}
            onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '') })}
          />
          {/* Said plainly, because a student who thinks this is a password will
              worry about choosing a good one. */}
          <span style={{ fontSize: 13, color: 'var(--slate)' }}>
            Pick any four digits. It is not a password — it only lets you back in if you
            close this page.
          </span>
        </div>

        {error && <div className="notice notice--bad">{error}</div>}

        <button className="btn" type="submit" disabled={!ready || connection !== 'open'}>
          {connection === 'open' ? 'Join' : 'Connecting…'}
        </button>
      </form>
    </div>
  );
}

function Live({
  state,
  connection,
  send,
}: {
  state: ParticipantState;
  connection: string;
  send: (e: ClientEvent) => void;
}) {
  const [picked, setPicked] = useState<OptionKey[]>([]);

  // Cleared whenever the question changes, so a selection never carries over.
  useEffect(() => setPicked(state.myChoice ?? []), [state.question?.id, state.myChoice]);

  const q = state.question;
  const locked = state.phase !== 'ANSWERING' || state.myChoice !== null;
  const need = q?.selectCount ?? 1;

  const toggle = (key: OptionKey) => {
    if (locked) return;
    setPicked((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      // For a single-answer question, tapping another option replaces rather
      // than refuses — that is what a radio does, and what a thumb expects.
      if (need === 1) return [key];
      return prev.length < need ? [...prev, key] : prev;
    });
  };

  const header = (
    <div className="session__bar">
      <span>{state.sessionName}</span>
      <span className={`link link--${connection}`}>{state.name}</span>
    </div>
  );

  if (state.status === 'PAUSED') {
    return (
      <div className="session">
        {header}
        <div className="curtain">
          <div>
            <h2>Paused</h2>
            <p>Your instructor will start again in a moment.</p>
          </div>
        </div>
      </div>
    );
  }

  if (state.status === 'COMPLETED') {
    return (
      <div className="session">
        {header}
        <div className="curtain">
          <div>
            <h2>That&apos;s the session</h2>
            <p>Thanks for taking part.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!q) {
    return (
      <div className="session">
        {header}
        <div className="curtain">
          <div>
            <h2>You&apos;re in</h2>
            <p>Waiting for the first question.</p>
          </div>
        </div>
      </div>
    );
  }

  const reveal = state.reveal;

  return (
    <div className="session">
      {header}
      <div className="session__body">
        {/*
          * Stem and options sit side by side once there is room for it. A DOP-C02
          * stem runs long and so do its options; stacking them in one narrow
          * column on a laptop means scrolling the stem out of sight to read what
          * you are choosing between. Below the breakpoint this collapses back to
          * a single column, which is the phone case and the common one.
          */}
        <div className="qa">
          <div className="qa__stem">
            <p className="qid tabular">{q.id}</p>
            <p className="stem" style={{ marginTop: 'var(--s-3)' }}>
              {q.stem}
            </p>
            {need > 1 && (
              <p style={{ marginTop: 'var(--s-3)', color: 'var(--slate)', fontSize: 14 }}>
                Choose {need}.
              </p>
            )}
          </div>

          <div className="options">
            {q.options.map((o) => {
              const isPicked = picked.includes(o.key);
              const isRight = reveal?.correct.includes(o.key) ?? false;
              const shownWrong = Boolean(reveal) && isPicked && !isRight;

              const cls = [
                'option',
                !reveal && isPicked ? 'option--picked' : '',
                reveal && isRight ? 'option--right' : '',
                shownWrong ? 'option--wrong' : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <button
                  key={o.key}
                  type="button"
                  className={cls}
                  disabled={locked}
                  aria-pressed={isPicked}
                  onClick={() => toggle(o.key)}
                >
                  <span className="option__key">{o.key}</span>
                  <span className="option__text">{o.text}</span>
                  {reveal && (
                    <span className="option__mark" aria-hidden="true">
                      {isRight ? '✓' : shownWrong ? '✗' : ''}
                    </span>
                  )}
                </button>
              );
            })}

            {state.phase === 'ANSWERING' && state.myChoice === null && (
              <button
                className="btn"
                disabled={picked.length !== need}
                onClick={() => send({ type: 'answer:submit', questionId: q.id, choice: picked })}
              >
                {picked.length === need ? 'Submit' : `Choose ${need - picked.length} more`}
              </button>
            )}

            {state.phase === 'ANSWERING' && state.myChoice !== null && (
              <p style={{ color: 'var(--slate)', fontSize: 15 }}>
                Answer locked in. Waiting for the room.
              </p>
            )}
          </div>
        </div>

        {/*
          * Nothing between the options and the justification.
          *
          * No verdict line — the ✓ on the correct option and the red on this
          * person's own pick already say it, and saying it twice turns a drill
          * into a score. No distribution either: what the rest of the room
          * picked is material for the instructor to work with out loud, not a
          * tally for a student to sit and read.
          */}

        {reveal?.justification && (
          <div className="explain">
            <p>{reveal.justification}</p>
            {reveal.whyNot && Object.keys(reveal.whyNot).length > 0 && (
              <div className="explain__why">
                {Object.entries(reveal.whyNot).map(([key, text]) => (
                  <div key={key}>
                    <b>{key}.</b> {text}
                  </div>
                ))}
              </div>
            )}
            {reveal.referenceUrl && (
              <a
                href={reveal.referenceUrl}
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--accent)', fontSize: 14 }}
              >
                AWS documentation →
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
